import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {CreateCustomerFromLead, CreatePotentialAmount, CreateTolerance, UpdateCustomer} from 'src/app/domain/command/customer/customer.command';
import {UploadVat} from 'src/app/domain/command/vat.command';
import {toFormData} from 'src/app/domain/function/form-data.helper';
import {objKeysToSnakeCase} from 'src/app/domain/function/object.helper';
import {Customer} from 'src/app/domain/model/customer/customer.model';
import CustomerStore from 'src/app/domain/store/customer/customer.store';
import {ApiClient} from 'src/app/infrastructure/http/api.client';

@Injectable({
  providedIn: 'root',
})
export default class CustomerApi {
  private readonly url = 'customer';
  private fetchInFlight: Promise<void> | null = null;
  private hasRequested = false;
  private generation = 0;

  constructor(private http: ApiClient, private store: CustomerStore) {
  }

  public fetch(): Promise<void> {
    this.hasRequested = true;

    if (!this.fetchInFlight) {
      const request: Promise<void> = this.request().finally(() => {
        if (this.fetchInFlight === request) {
          this.fetchInFlight = null;
        }
      });
      this.fetchInFlight = request;
    }

    return this.fetchInFlight;
  }

  public refresh(): Promise<void> {
    this.generation++;
    this.fetchInFlight = null;

    return this.fetch();
  }

  public invalidate(): void {
    this.generation++;
    this.fetchInFlight = null;
    this.hasRequested = false;
  }

  public async createFromLead(command: CreateCustomerFromLead): Promise<void> {
    const snakeCommand: object = objKeysToSnakeCase(command);
    await this.http.post(this.url, snakeCommand);
    this.refresh();
  }

  public customer(autoFetch = true): Observable<Customer | null> {
    return this.store.customer$.pipe(
      tap(() => {
        if (!this.hasRequested && autoFetch) {
          this.fetch();
        }
      }),
      filter(stored => !!stored)
    );
  }

  public syncCustomer(): Customer | null {
    return this.store.syncCustomer();
  }

  public async waitFetchAndSync(): Promise<Customer> {
    await this.fetch();
    return this.syncCustomer();
  }

  public async updatePotentialAmount(command: CreatePotentialAmount): Promise<void> {
    const snakeCommand: object = objKeysToSnakeCase(command);
    await this.http.put(this.url + '/' + command.customerId + '/potential-amounts', snakeCommand);
    this.refresh();
  }

  public async updateTolerance(command: CreateTolerance): Promise<void> {
    const snakeCommand: object = objKeysToSnakeCase(command);
    await this.http.put(this.url + '/' + command.customerId + '/tolerance', snakeCommand);
    this.refresh();
  }

  public async update(command: UpdateCustomer): Promise<void> {
    const snakeCommand: object = objKeysToSnakeCase(command);
    await this.http.put(this.url + '/' + command.id, snakeCommand);
    this.refresh();
  }

  public async uploadVat(command: UploadVat): Promise<void> {
    const user = await this.waitFetchAndSync();
    const formData = toFormData(command);

    await this.http.post(this.url + '/' + user.id + '/vat', formData);
    await this.refresh();
  }

  private async request(): Promise<void> {
    const generation = this.generation;

    try {
      const customer: Customer = await this.http.get(this.url);

      if (generation !== this.generation) {
        return this.fetchInFlight ?? undefined;
      }

      this.store.save(customer[0]);
    } catch (error) {
      if (generation === this.generation) {
        this.hasRequested = false;
      }

      throw error;
    }
  }
}
